import { useState, useEffect } from 'react';

/** Viewports at or below this height (px) are "short" — a phone held in landscape. */
export const SHORT_VIEWPORT_MAX_PX = 500;

/** Delay of the one re-read after a rotation, for engines whose size lands late. */
export const ROTATION_SETTLE_MS = 250;

const readIsShort = () => (
  typeof window !== 'undefined' ? window.innerHeight <= SHORT_VIEWPORT_MAX_PX : false
);

/**
 * True when the window is too short for the centered auth stack — i.e. a phone
 * in LANDSCAPE (~330–440px tall). Width keeps picking the layout mode
 * (`useLoginLayoutMode`); this flag only adapts the tablet branch's vertical
 * behavior.
 *
 * WHEN it re-evaluates (KAN-981, RD-LUI-016): only when the WIDTH changes or on
 * an orientation event — never on a height-only resize. Browsers that shrink
 * the page for the on-screen keyboard (Chrome / Brave / Firefox on iPhone,
 * Android, possibly the installed app) fire a `resize` whose height drops under
 * the threshold on a short phone; re-evaluating there flipped "short" mode
 * mid-typing, so the card re-laid out and glided under the user's fingers
 * (measured: card top moved 123px between 322ms and 850ms). A rotation always
 * changes the width, so `resize` still catches it; `orientationchange` and
 * `screen.orientation`'s `change` cover engines that fire those first (or
 * without a width-changing resize). Each of those also schedules ONE settle
 * re-read ~250ms later: some engines fire the orientation event before the new
 * size lands and never follow it with a width-changing resize. The threshold
 * itself is unchanged.
 */
export function useIsShortViewport() {
  const [short, setShort] = useState(readIsShort);
  useEffect(() => {
    let lastWidth = window.innerWidth;
    let settleTimer = null;
    const read = () => {
      lastWidth = window.innerWidth;
      setShort(readIsShort());
    };
    const evaluate = () => {
      read();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(read, ROTATION_SETTLE_MS);
    };
    // Keyboard open/close changes the height only — same width, no re-evaluation.
    const onResize = () => {
      if (window.innerWidth !== lastWidth) evaluate();
    };
    const orientation = window.screen && window.screen.orientation;
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', evaluate);
    if (orientation && typeof orientation.addEventListener === 'function') {
      orientation.addEventListener('change', evaluate);
    }
    return () => {
      clearTimeout(settleTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', evaluate);
      if (orientation && typeof orientation.removeEventListener === 'function') {
        orientation.removeEventListener('change', evaluate);
      }
    };
  }, []);
  return short;
}
